class QuickView {
  constructor() {
    this.modal = document.getElementById('bt-quick-view-product-modal');
    this.content = document.getElementById('bt-quick-view-product-content');
    this.isLoading = false;
    this.currentProductUrl = null;
    this.swiperMain = null;
    this.swiperThumbs = null;
    this.variantChangeUnsubscriber = null;
    this.abortController = undefined;
    this.pendingRequestUrl = null;

    this.selectors = {
      quickViewButton: '.bt-product-quick-view-btn',
      quickViewLoading: '.loading__spinner',
      galleryContainer: '.bt-quick-view-gallery',
      variantSelects: 'variant-selects',
      quantityForm: '.product-form__quantity',
      quantityInput: 'quantity-input',
    };
    this.init();

    MicroModal.init();
  }

  init = async () => {
    this.addEventToQuickViewButtons();
  };

  handleModalClose = (modal) => {
    if (modal.id === 'bt-quick-view-product-modal') {
      this.cleanup();
    }
  };

  addEventToQuickViewButtons = () => {
    addEventDelegate({
      selector: this.selectors.quickViewButton,
      handler: async (e, btn) => {
        e.preventDefault();
        if (this.isLoading) return;
        this.toggleLoading(true, btn);

        const productHandle = btn && btn.dataset.productHandle;
        if (productHandle) {
          const url = formatUrl('products', productHandle, 'view=quickview-item');
          this.currentProductUrl = url;
          const prodHTML = await fetchCache(url);
          this.content.innerHTML = prodHTML;
          MicroModal.show(this.modal.id);

          this.initQuickViewFeatures();
          this.initVariantChangeHandler();

          this.toggleLoading(false, btn);
          this.isLoading = false;
        } else {
          this.content.innerHTML = 'No product found';

          this.toggleLoading(false, btn);
          this.isLoading = false;
        }
      },
    });
  };

  initQuickViewFeatures() {
    // Initialize Swiper sliders only if elements exist
    const thumbsElement = this.content.querySelector('.swiper-thumbs');
    const mainElement = this.content.querySelector('.swiper-main');

    if (thumbsElement) {
      this.swiperThumbs = new Swiper(thumbsElement, {
        spaceBetween: 10,
        slidesPerView: 5,
        freeMode: true,
        watchSlidesProgress: true,
        loop: false,
      });
    }

    if (mainElement) {
      this.swiperMain = new Swiper(mainElement, {
        spaceBetween: 10,
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
        thumbs: this.swiperThumbs ? { swiper: this.swiperThumbs } : undefined,
        loop: false,
      });
    }

    // Add thumbnail click handlers if both sliders exist
    if (this.swiperThumbs && this.swiperMain) {
      this.swiperThumbs.slides.forEach((slide, index) => {
        slide.addEventListener('click', () => {
          this.swiperThumbs.slideTo(index, 300);
        });
      });
    }

    // Initialize wishlist and compare buttons state
    this.initWishlistCompareButtons();
  }

  initVariantChangeHandler() {
    const variantSelects = this.content.querySelector(this.selectors.variantSelects);
    if (variantSelects) {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.handleVariantChange.bind(this)
      );
    }
  }

  initWishlistCompareButtons() {
    // Initialize wishlist buttons state
    if (UtenzoTheme.Wishlist) {
      UtenzoTheme.Wishlist.setWishlistButtonsState();
    }

    // Initialize compare buttons state
    if (UtenzoTheme.CompareProduct) {
      UtenzoTheme.CompareProduct.setCompareButtonsState();
    }
  }

  handleVariantChange(event) {
    const {
      data: { event: changeEvent, target, selectedOptionValues },
    } = event;

    // Only handle events from within the quick view modal
    if (!this.content.contains(changeEvent.target)) return;

    if (!selectedOptionValues || selectedOptionValues.length === 0) return;

    // Build URL with selected variant options
    const variantUrl = this.buildRequestUrlWithParams(this.currentProductUrl, selectedOptionValues);
    this.renderProductInfo({
      requestUrl: variantUrl,
      targetId: target.id,
      callback: this.handleUpdateProductInfo.bind(this),
    });
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }
    return `${url}&${params.join('&')}`;
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        callback(html);
      })
      .then(() => {
        // set focus to last clicked option value
        document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  handleUpdateProductInfo(html) {
    const variant = this.getSelectedVariant(html);

    if (!variant) {
      return;
    }

    // Update price
    this.updatePrice(html);

    // Update inventory
    this.updateInventory(html);

    // Update media gallery
    this.updateMedia(html, variant?.featured_media?.id);

    // Update quantity form
    this.updateQuantityForm(html);

    // Update variant inputs
    this.updateVariantInputs(variant?.id);

    // Publish variant change event for other components
    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        variantId: variant?.id,
        productUrl: this.currentProductUrl,
        source: 'quick-view',
        variant,
      },
    });
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updatePrice(html) {
    const updateSourceFromDestination = (id) => {
      const source = html.getElementById(`${id}-${this.getProductId()}`);
      const destination = this.content.querySelector(`#${id}-${this.getProductId()}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
      }
    };
    updateSourceFromDestination('price');
  }

  updateInventory(html) {
    const updateSourceFromDestination = (id) => {
      const source = html.getElementById(`${id}-${this.getProductId()}`);
      const destination = this.content.querySelector(`#${id}-${this.getProductId()}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', source.classList.contains('hidden'));
      }
    };
    updateSourceFromDestination('Inventory');
  }

  updateMedia(html, variantFeaturedMediaId) {
    if (!variantFeaturedMediaId || !this.swiperMain) return;

    const currentSlideIndex = this.swiperMain.slides.findIndex(
      (slide) => slide.getAttribute('data-media-id') === variantFeaturedMediaId.toString()
    );

    if (currentSlideIndex >= 0 && currentSlideIndex < this.swiperMain.slides.length) {
      this.swiperMain.slideTo(currentSlideIndex);
    }
  }

  updateQuantityForm(html) {
    const updateSourceFromDestination = (id) => {
      const source = html.getElementById(`${id}-${this.getProductId()}`);
      const destination = this.content.querySelector(`#${id}-${this.getProductId()}`);
      if (source && destination) {
        destination.innerHTML = source.innerHTML;
      }
    };

    updateSourceFromDestination('Quantity-Form');
    this.updateQuantityRules(html);
  }

  updateQuantityRules(html) {
    const quantityForm = this.content.querySelector(this.selectors.quantityForm);
    if (!quantityForm) return;

    const quantityInput = quantityForm.querySelector(this.selectors.quantityInput);
    if (!quantityInput) return;

    const quantityFormUpdated = html.getElementById(`Quantity-Form-${this.getProductId()}`);
    if (!quantityFormUpdated) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          } else {
            current.removeAttribute(attribute);
          }
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  updateVariantInputs(variantId) {
    if (!variantId) return;

    this.content
      .querySelectorAll(`#product-form-${this.getProductId()}, #product-form-installment-${this.getProductId()}`)
      .forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = variantId ?? '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
  }

  getProductId() {
    const productInfo = this.content.querySelector('.bt-quick-view-wrapper');
    return productInfo ? productInfo.dataset.productId : null;
  }

  // Toggle loading spinner on the target element
  toggleLoading(isLoading, btn) {
    const loading = btn.querySelector(this.selectors.quickViewLoading);
    if (isLoading) {
      loading.classList.remove('hidden');
    } else {
      loading.classList.add('hidden');
    }
  }

  // Cleanup method to unsubscribe from events
  cleanup() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.swiperMain) {
      this.swiperMain.destroy();
      this.swiperMain = null;
    }

    if (this.swiperThumbs) {
      this.swiperThumbs.destroy();
      this.swiperThumbs = null;
    }

    if (this.content) {
      this.content.innerHTML = '';
    }

    // Reset current product URL
    this.currentProductUrl = null;
    this.pendingRequestUrl = null;
  }
}

UtenzoTheme.QuickView = new QuickView();
